import { useState } from 'react';
import { PageHeader, Button, Badge } from 'components/ui';
import { LoadingState } from 'components/feedback';
import { PlusIcon } from 'components/icons';
import { useToast } from 'components/toast/ToastProvider';
import { useVendor } from 'features/vendor';
import { useUpdateVendorProfile } from '../hooks/useUpdateVendorProfile';
import { ShopForm } from '../components/ShopForm';
import { ShopList } from '../components/ShopList';
import { AddShopModal } from '../components/AddShopModal';
import { profileToForm } from '../utils/shopProfile';

/**
 * Shop management: list all shops (MY_SHOPS), edit the active shop, and add a
 * new shop. `updateProfile` edits the active shop (no shop id arg), so the form
 * below targets the active one; the list shows which shop that is.
 */
export function ShopProfilePage() {
  const { profile, shops, loading: profileLoading, refetch: refetchShops } = useVendor();
  const { updateShop, loading: saving } = useUpdateVendorProfile();
  const toast = useToast();

  const [addOpen, setAddOpen] = useState(false);

  const handleEdit = async (input) => {
    try {
      const payload = await updateShop(input);
      if (payload?.success) {
        toast.success('Shop updated', payload.message || 'Your shop details were saved.');
        refetchShops();
      } else {
        toast.error(
          'Could not update shop',
          payload?.errors?.[0]?.message || payload?.message || 'Please try again.',
        );
      }
    } catch (err) {
      toast.error('Something went wrong', err.message);
    }
  };

  const handleAdd = async (input) => {
    try {
      const payload = await updateShop(input); // input carries registerNewShop: true
      if (payload?.success) {
        toast.success('Shop added', payload.message || 'Your new shop was created.');
        setAddOpen(false);
        refetchShops();
      } else {
        toast.error(
          'Could not add shop',
          payload?.errors?.[0]?.message || payload?.message || 'Please try again.',
        );
      }
    } catch (err) {
      toast.error('Something went wrong', err.message);
    }
  };

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader
        title="Shop"
        description="Your shops, profile and preferences."
        actions={
          <Button leftIcon={<PlusIcon className="h-4 w-4" />} onClick={() => setAddOpen(true)}>
            Add shop
          </Button>
        }
      />

      {shops.length > 0 && (
        <section className="mb-8">
          <h2 className="mb-2 text-sm font-semibold text-slate-700">Your shops</h2>
          <ShopList shops={shops} activeShopId={profile?.id} />
        </section>
      )}

      <section>
        <div className="mb-3 flex items-center gap-2">
          <h2 className="text-sm font-semibold text-slate-700">Shop details</h2>
          {profile &&
            (profile.isVerified ? (
              <Badge tone="success">Verified</Badge>
            ) : (
              <Badge tone="warning">Unverified</Badge>
            ))}
        </div>

        {profileLoading && !profile ? (
          <LoadingState label="Loading shop…" />
        ) : (
          <ShopForm
            key={profile?.id || 'active'}
            initialValues={profileToForm(profile)}
            onSubmit={handleEdit}
            submitting={saving}
            submitLabel="Save changes"
          />
        )}
      </section>

      <AddShopModal
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        onSubmit={handleAdd}
        submitting={saving}
      />
    </div>
  );
}

export default ShopProfilePage;
